import { HttpClient, HttpHeaders, HttpParams, HttpContext } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import { APP_CONFIG } from 'libs/shared/app-config/src/lib/gb-app-config'
import { Observable } from 'rxjs'

// Angular CLI configuration thing.
export interface IRequestOptions {
  headers?: HttpHeaders
  observe?: 'body'
  params?: HttpParams
  reportProgress?: boolean
  responseType?: 'json'
  withCredentials?: boolean
  body?: any
  context?: HttpContext
}

/**
 * HttpClient encapsultion, this service is to avoid the repetition of
 * {$config.apiUrl}+/url/whatever/it/is.
 *
 * all the http methods are available respecting the httpClient params headers etc
 */
@Injectable({
  providedIn: 'root',
})
export class GbBaseApiService {
  defaultOptions: IRequestOptions = {
    withCredentials: true,
    headers: new HttpHeaders({
      'Cache-Control': 'no-cache',
    }),
    params: new HttpParams(),
  }

  constructor(protected http: HttpClient, @Inject(APP_CONFIG) protected appConfig: any) {}

  /**
   * private method to centralise the config api url
   * @returns api url
   */
  protected getApiUrl() {
    return this.appConfig.apiUrl
  }

  /**
   * GET request
   * @param {string} endPoint it doesn't need / in front of the end point
   * @param {IRequestOptions} options options of the request like headers, body, etc.
   * @returns {Observable<T>}
   *
   * increment query param is being appended to the URL so that the app will fetch new resources on each request
   * the endpoint request name is included just so that we can see easily which request is being made in the dev tools
   *
   * This is an issue with Pusher, new get requests are not requesting from the api once Pusher is instantiated without the added
   * increment, this needs to be removed once we find a way to fix this issue.
   */
  public get<T>(endPoint: string, options: IRequestOptions = {}): Observable<T> {
    const modifiedOptions = {
      ...this.defaultOptions,
      ...options,
    }

    return this.http.get<T>(this.getApiUrl() + endPoint, modifiedOptions)
  }

  /**
   * POST request
   * @param {string} endPoint end point of the api
   * @param {any} params body of the request.
   * @param {IRequestOptions} options options of the request like headers, body, etc.
   * @returns {Observable<T>}
   */
  public post<T>(endPoint: string, params: any = {}, options: IRequestOptions = {}): Observable<T> {
    const modifiedOptions = { ...this.defaultOptions, ...options }
    return this.http.post<T>(this.getApiUrl() + endPoint, params, modifiedOptions)
  }

  /**
   * PUT request
   * @param {string} endPoint end point of the api
   * @param {Object} params body of the request.
   * @param {IRequestOptions} options options of the request like headers, body, etc.
   * @returns {Observable<T>}
   */
  public put<T>(endPoint: string, params: any, options: IRequestOptions): Observable<T> {
    const modifiedOptions = { ...this.defaultOptions, options }
    return this.http.put<T>(this.getApiUrl() + endPoint, params, modifiedOptions)
  }

  /**
   * DELETE request
   * @param {string} endPoint end point of the api
   * @param {IRequestOptions} options options of the request like headers, body, etc.
   * @returns {Observable<T>}
   */
  public delete<T>(endPoint: string, options: IRequestOptions = {}): Observable<T> {
    const modifiedOptions = { ...this.defaultOptions, options }
    return this.http.delete<T>(this.getApiUrl() + endPoint, modifiedOptions)
  }

  /**
   * patch request
   * @param {string} endPoint end point of the api
   * @param {Object} params body of the request.
   * @param {IRequestOptions} options options of the request like headers, body, etc.
   * @returns {Observable<T>}
   */
  public patch<T>(endPoint: string, params: any, options: IRequestOptions = {}): Observable<T> {
    const modifiedOptions = { ...this.defaultOptions, ...options }
    return this.http.patch<T>(this.getApiUrl() + endPoint, params, modifiedOptions)
  }

  public download(endPoint: string) {
    return this.http.get(this.getApiUrl() + endPoint, {
      responseType: 'blob',
      withCredentials: true,
    })
  }

  public getText(endPoint: string) {
    return this.http.get(this.getApiUrl() + endPoint, {
      responseType: 'text',
      withCredentials: true,
    })
  }
}
